import { SMSMessageDto } from '@act/shared/data-transfer-objects';
import { Button, FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  Divider,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { smsMessageRequests } from 'redux/requests/sms-message.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import { z } from 'zod';
import { useToast } from '../app/ReusableToast';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from '../dialog-forms/components/SharedDialogHookComponents';
import useUnsavedChangesDialog from '../dialog-forms/hooks/use-unsaved-changes-dialog';
import Loader from '../workflows/loader';

export interface AssignTextMessageUniqueProps {
  sms: SMSMessageDto;
}

export type AssignTextMessageDialogProps = GenericDialogProps<boolean> &
  AssignTextMessageUniqueProps;

export type AssignTextMessageDialogCallbackInput = {
  // Empty type
};

const AssignTextMessageFormSchema = z.object({
  memberId: z.string().min(1, 'Please select a member'),
});

export type AssignTextMessageForm = z.infer<typeof AssignTextMessageFormSchema>;

const AssignTextMessageDialog = ({
  sms,
  open,
  setOpen,
}: AssignTextMessageDialogProps) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const [membersResponse, _isLoadingMembers, getMembers] = useApiCall(
    smsMessageRequests.getMembersForSMSAssignment
  );

  useEffect(() => {
    if (open && sms?.id) {
      getMembers({ params: { id: sms.id } });
    }
  }, []);

  const members = membersResponse?.users || [];

  const { control, handleSubmit, reset, formState } =
    useForm<AssignTextMessageForm>({
      mode: 'onChange',
      resolver: zodResolver(AssignTextMessageFormSchema),
      defaultValues: {
        memberId: '',
      },
    });

  const [assignResult, isAssigning, assignSMSMessage] = useApiCall(
    smsMessageRequests.assignSMSMessage
  );

  const isSubmitting = isAssigning;

  // Handle successful assignment
  useEffect(() => {
    if (assignResult && !isAssigning) {
      toast('Text message assigned successfully', 'success');
      reset();
      setOpen(false);
    }
  }, [assignResult, isAssigning, reset, setOpen]);

  const { isDirty } = formState;

  const handleClose = useCallback(
    (_event?: unknown, _reason?: string) => {
      // Prevent closing during submission
      if (isSubmitting) {
        return;
      }

      // Check for unsaved changes and show confirmation dialog if needed
      if (isDirty) {
        void handleUnsavedChanges(() => {
          reset();
          setOpen(false);
        });
      } else {
        reset();
        setOpen(false);
      }
    },
    [handleUnsavedChanges, isDirty, reset, setOpen, isSubmitting]
  );

  const onSubmit = (data: AssignTextMessageForm) => {
    assignSMSMessage({
      params: { id: sms?.id },
      body: { subjectId: data.memberId },
    });
  };

  const memberOptions = useMemo(
    () =>
      [...members]
        .reverse()
        .sort((memberA, memberB) => {
          if (memberA.name < memberB.name) {
            return -1;
          } else if (memberA.name > memberB.name) {
            return 1;
          } else {
            return 0;
          }
        })
        .map(member => ({
          label: member.name,
          value: member.id,
        })),
    [members]
  );

  const formConfig: FormConfig<AssignTextMessageForm> = useMemo(
    () => [
      {
        type: 'input',
        name: 'memberId',
        label: 'Select a member',
        input: 'select',
        props: {
          options: memberOptions,
          required: true,
          fullWidth: true,
        },
      },
    ],
    [memberOptions]
  );

  return (
    <div style={{ width: '600px' }}>
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogContentContainer>
          <DialogTitleBarComponent
            title="Assign text message"
            handleClose={handleClose}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent sx={{ padding: '12px 24px 0' }}>
              {!members?.length && <Loader />}
              {members?.length && (
                <>
                  <Typography
                    variant="titleMedium"
                    sx={{ display: 'block', marginBottom: '16px' }}
                  >
                    Select a member for this text message
                  </Typography>
                  <FormBuilder
                    config={formConfig}
                    control={control}
                    formState={formState}
                    disabled={isSubmitting}
                  />
                </>
              )}
            </CardContent>
            <Divider sx={{ marginTop: '48px' }} />
            <CardActions
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'right',
                padding: '8px 24px',
              }}
            >
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                disabled={!formState.isValid || isSubmitting}
                debounced
                type="submit"
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? 'Assigning...' : 'Assign message'}
              </Button>
            </CardActions>
          </form>
        </DialogContentContainer>
      </Dialog>
    </div>
  );
};

export default AssignTextMessageDialog;
